package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.agent.RisponditoreDomande.creatoreTipo
import com.heelion.farmafreelance.models.AnnuncioStato
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.CreatoreTipo
import com.heelion.farmafreelance.models.Match
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.UserRole
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * Le risposte a una proposta, con il codice azione usato dall'app (AuthViewModel.respondToMatch)
 * e lo stato in cui sono possibili, come i pulsanti della Home.
 */
enum class AzioneProposta(val codice: String, val verbo: String, val etichetta: String) {
    ACCETTA("ACCETTA", "accettare", "Accetta"),
    RIFIUTA("RIFIUTA", "rifiutare", "Rifiuta"),
    CONFERMA("CONFERMA", "confermare definitivamente", "Conferma"),
    RITIRA("LIBERA", "ritirare la tua accettazione per", "Ritira accettazione"),
    DISDICI("DISDICI", "disdire", "Disdici");

    /** Azioni che annullano qualcosa: dai pulsanti manuali chiedono prima conferma. */
    val annulla: Boolean get() = this == RIFIUTA || this == RITIRA || this == DISDICI

    /** Se [m] è in uno stato in cui questa azione è permessa a chi ha ruolo [mio]. */
    fun possibile(m: Match, mio: CreatoreTipo): Boolean = when (this) {
        ACCETTA, RIFIUTA -> m.stato == MatchStato.PROPOSTO && m.propostoDa != mio
        CONFERMA -> m.stato == MatchStato.ACCETTATO && m.propostoDa == mio
        RITIRA -> m.stato == MatchStato.ACCETTATO && m.propostoDa != mio
        DISDICI -> m.stato == MatchStato.CONFERMATO
    }

    companion object {
        /** Le azioni possibili adesso su [m], nell'ordine in cui mostrarle (es. Accetta, Rifiuta). */
        fun possibili(m: Match, mio: CreatoreTipo): List<AzioneProposta> = entries.filter { it.possibile(m, mio) }
    }
}

/** Azioni che Farmy esegue nell'app, sempre dopo un "sì" dell'utente (vedi Assistente). */
sealed class AzioneFarmy {
    /** [azione] null = non si è capito se accettare, rifiutare, ... */
    data class RispondiProposta(val azione: AzioneProposta?, val testo: String) : AzioneFarmy()
    data class ProponiTurno(val nome: String) : AzioneFarmy()
    /** [nome] vuoto = il proprio profilo. */
    data class ApriProfilo(val nome: String) : AzioneFarmy()
    data class Preferito(val nome: String, val aggiungi: Boolean) : AzioneFarmy()
    /** "cerco farmacista galenico a 35 € l'ora di sabato": Farmy cerca e propone i migliori (solo farmacie). */
    data class TrovaFarmacista(val testo: String) : AzioneFarmy()
    /** "Avvisami quando c'è un turno a Roma il sabato": salva una ricerca, il server avvisa (vedi AvvisiFarmy). */
    data class SalvaAvviso(val testo: String) : AzioneFarmy()
    /**
     * "mi accetti il turno alla Farmacia Rossi di sabato 5 novembre": accettare un turno APERTO
     * delle controparti (come il pulsante "Accetta" dei turni urgenti in Home), non una proposta.
     */
    data class PrendiTurno(val testo: String) : AzioneFarmy()
}

object InterpreteAzioni {
    // Davanti al verbo: "mi accetti…", "puoi accettarmi…", "vorrei accettare…".
    private const val CORTESIA = "^(?:ok |va bene |si )?(?:(?:mi|me lo|puoi|potresti|voglio|vorrei|posso) )*"
    private val VERBO_RISPOSTA = Regex(
        "$CORTESIA(accett|rifiut|conferm|ritir|disdic|disdire)\\w*\\b.*\\b(turn|propost|quell)"
    )
    private val VERBO_PRENDI = Regex(
        "$CORTESIA(prend|candid|prenot)\\w*\\b.*\\b(turn|quell|emergenz|urgent)|\\bmi interessa\\b.*\\bturn"
    )
    private val URGENTE = Regex("\\b(urgent\\w*|emergenz\\w*|sos)\\b")

    /** "prendi il turno della Farmacia Rossi", "mi candido per il turno urgente": accettare un turno aperto. */
    fun ePrendiTurno(testo: String): Boolean = VERBO_PRENDI.containsMatchIn(IntentClassifier.normalizza(testo))

    fun soloUrgenti(testo: String): Boolean = URGENTE.containsMatchIn(IntentClassifier.normalizza(testo))

    /**
     * I turni aperti delle controparti che corrispondono a quanto detto: ancora prenotabili, senza
     * già un mio match attivo, del giorno [data] (se detto), di chi è nominato (nome o località, se
     * detti) e, se si parla di urgenza, che iniziano entro 24 ore. Dal più vicino nel tempo.
     */
    fun turniDaPrendere(
        annunci: List<AnnuncioTurno>,
        matches: List<Match>,
        testo: String,
        data: LocalDate?,
        oggi: LocalDate,
        adesso: Instant
    ): List<AnnuncioTurno> {
        val attivi = matches.filter { it.stato != MatchStato.RIFIUTATO && it.stato != MatchStato.ANNULLATO }.map { it.annuncioId }.toSet()
        val cercato = nomeCercato(testo).split(" ").filter { it.length >= 3 }
        val urgenti = soloUrgenti(testo)
        return annunci
            .filter { it.stato == AnnuncioStato.APERTO && it.postiOccupati < it.quantitaMassima && it.id != null && it.id !in attivi }
            .filter { giorno(it) >= oggi && (data == null || giorno(it) == data) }
            .filter { a ->
                val parole = listOfNotNull(a.nomeCreatore, a.localitaCreatore, a.zonaTestuale)
                    .flatMap { IntentClassifier.normalizza(it).split(" ") }.toSet()
                cercato.isEmpty() || cercato.any { it in parole }
            }
            .filter { !urgenti || EmergenzeFarmy.urgente(it, adesso) }
            .sortedBy { it.dataTurno }
    }

    /** true se la frase dice quale turno (un nome, una località, un giorno o "urgente"). */
    fun indicaUnTurno(testo: String, data: LocalDate?): Boolean =
        data != null || soloUrgenti(testo) || nomeCercato(testo).split(" ").any { it.length >= 3 }

    /** "il turno di giovedì 5 novembre 8:00-20:00 della Farmacia Rossi (Milano), 30 €/h" */
    fun descriviTurno(a: AnnuncioTurno, oggi: LocalDate): String = buildString {
        append("il turno di ${RisponditoreDomande.formattaGiorno(giorno(a), oggi)} ${a.oraInizio}-${a.oraFine}")
        a.nomeCreatore?.takeIf { it.isNotBlank() }?.let { append(" di $it") }
        (a.localitaCreatore ?: a.zonaTestuale)?.takeIf { it.isNotBlank() }?.let { append(" ($it)") }
        a.compenso?.takeIf { it > 0 }?.let { append(", ${RisponditoreDomande.formattaEuro(it)}/h") }
    }

    private fun giorno(a: AnnuncioTurno) = a.dataTurno.toLocalDateTime(TimeZone.UTC).date

    /**
     * "conferma il turno di sabato", "accetto la proposta della farmacia Rossi": riconosciuta con
     * una regola e non col modello, perché il parser da solo la leggerebbe come CREAZIONE di un
     * turno. "conferma" da solo resta la conferma dell'azione in sospeso (manca l'oggetto).
     */
    fun eRispostaProposta(testo: String): Boolean = VERBO_RISPOSTA.containsMatchIn(IntentClassifier.normalizza(testo))

    fun azioneProposta(testo: String): AzioneProposta? {
        val t = IntentClassifier.normalizza(testo)
        return when {
            Regex("\\b(ritir|libera)").containsMatchIn(t) -> AzioneProposta.RITIRA
            Regex("\\b(disdic|disdire|disdett)").containsMatchIn(t) -> AzioneProposta.DISDICI
            Regex("\\b(rifiut|di no\\b|dico di no)").containsMatchIn(t) -> AzioneProposta.RIFIUTA
            Regex("\\b(conferm|definitiv)").containsMatchIn(t) -> AzioneProposta.CONFERMA
            Regex("\\baccett").containsMatchIn(t) -> AzioneProposta.ACCETTA
            else -> null
        }
    }

    fun preferitoAggiungi(testo: String): Boolean =
        !Regex("\\b(togli|rimuovi|elimina|cancella|non voglio|non piu)").containsMatchIn(IntentClassifier.normalizza(testo))

    // Parole che non fanno parte del nome della farmacia/persona cercata.
    private val NON_NOME = setOf(
        "apri", "aprimi", "fammi", "vedere", "mostrami", "vorrei", "voglio", "il", "lo", "la", "i", "gli", "le", "di", "del",
        "della", "dello", "dei", "delle", "a", "al", "alla", "allo", "ai", "alle", "da", "dal", "dalla", "dai", "con",
        "per", "tra", "fra", "nei", "nelle", "come", "profilo", "scheda", "dettagli", "info", "chi", "e", "proponi",
        "proponiti", "manda", "mandami", "invia", "inviami", "fai", "una", "un", "uno", "turno", "turni", "proposta",
        "diretta", "contatta", "chiedi", "scrivi", "proporre", "aggiungi", "metti", "salva", "segna", "togli", "rimuovi",
        "elimina", "preferiti", "preferito", "preferita", "non", "piu", "mio", "miei", "mia", "favore", "farmacia",
        "farmacista", "dottor", "dottore", "dottoressa", "dott", "dr", "sono", "disponibile", "che", "vuoi",
        // verbi delle risposte alle proposte e parole di cortesia
        "accetta", "accetto", "accettare", "accettazione", "rifiuta", "rifiuto", "rifiutare", "conferma", "confermo",
        "confermami", "confermare", "ritira", "ritiro", "disdici", "disdico", "disdire", "rendi", "definitivo",
        "quella", "quello", "ok", "va", "bene", "si", "no", "dico", "dì",
        // "mi accetti il turno urgente", "prendi quel turno", "mi candido"
        "mi", "me", "accetti", "accettami", "accettarmi", "accettalo", "prendi", "prendo", "prendimi", "prendere",
        "prenota", "prenotami", "candida", "candidami", "candido", "candidarmi", "interessa", "puoi", "potresti",
        "posso", "quel", "urgente", "urgenti", "emergenza", "emergenze", "sos", "presso", "aperto",
        // parole di data (la data si confronta a parte)
        "oggi", "domani", "dopodomani", "prossimo", "prossima", "lunedi", "martedi", "mercoledi", "giovedi", "venerdi",
        "sabato", "domenica", "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto",
        "settembre", "ottobre", "novembre", "dicembre", "mattina", "pomeriggio", "sera"
    )

    /** "apri il profilo della farmacia Rossi" -> "rossi"; "" se non c'è un nome (= il proprio profilo). */
    fun nomeCercato(testo: String): String =
        IntentClassifier.normalizza(testo).split(" ")
            .filter { it.isNotBlank() && it !in NON_NOME && !it.all { c -> c.isDigit() } }
            .joinToString(" ")

    /**
     * I match su cui [azione] è possibile che corrispondono a quanto detto: data ([data], se
     * detta) e nome della controparte (se una sua parola compare nella frase).
     */
    fun candidati(
        azione: AzioneProposta,
        ruolo: UserRole,
        matches: List<Match>,
        testo: String,
        data: LocalDate?
    ): List<Match> {
        val mio = ruolo.creatoreTipo() ?: return emptyList()
        val parole = IntentClassifier.normalizza(testo).split(" ").toSet()
        return matches.filter { azione.possibile(it, mio) }
            .filter { data == null || it.dataTurno?.toLocalDateTime(TimeZone.UTC)?.date == data }
            .filter { m ->
                val nome = m.nomeControparte?.let { IntentClassifier.normalizza(it).split(" ") }.orEmpty()
                    .filter { it.length >= 3 && it !in NON_NOME }
                // Se la frase nomina qualcuno, deve essere questa controparte; se no vanno bene tutti.
                nomeCercato(testo).isBlank() || nome.any { it in parole }
            }
    }

    /** "la proposta della Farmacia Rossi per il 26/9" */
    fun descrivi(m: Match): String {
        val chi = m.nomeControparte?.takeIf { it.isNotBlank() } ?: "la controparte"
        val quando = m.dataTurno?.toLocalDateTime(TimeZone.UTC)?.date?.let { " per il ${it.dayOfMonth}/${it.monthNumber}" }.orEmpty()
        return "il turno con $chi$quando"
    }
}
